import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from './ui/dialog';
import { Camera, RefreshCw, Check, X, Loader2, CameraOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Selfie capture modal for clock-in/clock-out compliance.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - onCaptured: ({ url, storage_path, fallback }) => void
 *  - bookingId: string
 *  - kind: 'check_in' | 'check_out'
 *
 * Iter-149 P0 fallback flow retained.
 *
 * Iter-342 — Cloudflare 520 mitigation:
 *  - Client-side JPEG re-compression at max 1280 px + 0.85 quality so
 *    modern phones don't ship 6 MB HEIC through Cloudflare.
 *  - Real upload progress bar (XMLHttpRequest under the hood).
 *  - Use Photo button disabled during upload — no accidental double-tap.
 *  - Selfie preserved on failure; explicit Retry button re-uploads the
 *    same in-memory snapshot without asking the user to retake.
 *  - Structured error responses (`retryable` flag) drive 1 automatic
 *    retry, then a manual Retry button surfaces.
 *  - `X-Client-Request-ID` header echoed to the server so a manual
 *    retry doesn't create a duplicate storage object.
 */

// Iter-342 — compress an image to a target max dimension + quality so a
// mobile 12MP photo (~5MB) becomes ~350KB. Only applied when the source
// is above the threshold — small images pass through unchanged.
async function compressDataUrl(dataUrl, { maxDim = 1280, quality = 0.85 } = {}) {
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = dataUrl;
    });
    const { width, height } = img;
    if (width <= maxDim && height <= maxDim) return dataUrl;
    const scale = maxDim / Math.max(width, height);
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', quality);
  } catch (_e) {
    return dataUrl; // never break the flow on a resize hiccup
  }
}

async function dataUrlToBlob(dataUrl) {
  return await (await fetch(dataUrl)).blob();
}

const SelfieCapture = ({ open, onClose, onCaptured, bookingId, kind = 'check_in' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  // Iter-342 — stable client_request_id per capture session so a
  // manual retry hits the same server-side upload slot and can never
  // create a duplicate selfie object.
  const requestIdRef = useRef(null);
  // Preserve the last successful storage result so a retry after a
  // clock-in failure can reuse it (parent decides — we hand it back).
  const lastResultRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | starting | capturing | preview | uploading | upload_failed
  const [snapshot, setSnapshot] = useState(null); // data URL
  const [cameraError, setCameraError] = useState(null);
  const [uploadError, setUploadError] = useState(null); // { message, retryable }
  const [progress, setProgress] = useState(0);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setStatus('starting');
    // Iter-149 — hard timeout in case getUserMedia hangs (e.g. native
    // permission prompt stuck, WebView deadlock, denied silently).
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      setCameraError(
        'Camera did not respond after 8 seconds. You can retry or continue without a selfie (admin review required).'
      );
      setStatus('idle');
    }, 8000);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      clearTimeout(timeout);
      if (timedOut) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      setStatus('capturing');
      await new Promise((r) => requestAnimationFrame(() => r()));
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch (_) { /* ignore */ }
      }
    } catch (e) {
      clearTimeout(timeout);
      setCameraError(
        e?.name === 'NotAllowedError'
          ? 'Camera access denied. Enable camera in your browser settings, or continue without a selfie (admin review required).'
          : 'Unable to access camera. Try again, or continue without a selfie (admin review required).'
      );
      setStatus('idle');
    }
  }, []);

  const openSession = useCallback(() => {
    setSnapshot(null);
    setUploadError(null);
    setProgress(0);
    // Fresh client_request_id for every open — a new "Capture" attempt
    // is a new logical intent.
    requestIdRef.current = (window.crypto?.randomUUID?.() || `sc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    lastResultRef.current = null;
    setStatus('idle');
    startCamera();
  }, [startCamera]);

  useEffect(() => {
    if (open) {
      openSession();
    } else {
      stopStream();
    }
    return () => stopStream();
  }, [open, openSession, stopStream]);

  useEffect(() => {
    if (status === 'capturing' && videoRef.current && streamRef.current && videoRef.current.srcObject !== streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [status]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 640;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // Mirror horizontally so the saved photo matches what the user sees
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setSnapshot(dataUrl);
    setStatus('preview');
    setUploadError(null);
    stopStream();
  };

  const retake = () => {
    setSnapshot(null);
    setUploadError(null);
    lastResultRef.current = null;
    // NEW client_request_id — this is a genuinely new intent
    requestIdRef.current = (window.crypto?.randomUUID?.() || `sc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    startCamera();
  };

  // Iter-342 — actual upload with progress + one auto-retry on retryable failures.
  const uploadOnce = useCallback(async (blob, opts = {}) => {
    const fd = new FormData();
    fd.append('file', new File([blob], `${kind}_${Date.now()}.jpg`, { type: 'image/jpeg' }));
    const res = await api.post(
      `/attendance/upload-selfie?booking_id=${bookingId}&kind=${kind}`,
      fd,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Client-Request-ID': requestIdRef.current,
        },
        timeout: 60000, // Under Cloudflare's ~100s ceiling by a safe margin
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
        // Don't throw on retryable statuses — we handle them.
        validateStatus: () => true,
      }
    );
    return res;
  }, [bookingId, kind]);

  const runUpload = useCallback(async () => {
    if (!snapshot) return;
    setUploadError(null);
    setStatus('uploading');
    setProgress(0);
    try {
      const compressed = await compressDataUrl(snapshot);
      const blob = await dataUrlToBlob(compressed);

      let res = await uploadOnce(blob);
      // Iter-342 — auto-retry ONCE on retryable failures. Structured
      // error body carries `retryable: true` for 5xx / network faults.
      if (res.status >= 400 && res.data?.retryable) {
        setProgress(0);
        await new Promise((r) => setTimeout(r, 800));
        res = await uploadOnce(blob);
      }

      if (res.status >= 200 && res.status < 300) {
        const result = { url: res.data.url, storage_path: res.data.storage_path };
        lastResultRef.current = result;
        onCaptured?.(result);
        toast.success('Selfie captured');
        onClose();
        return;
      }
      // Failure that persists after one auto-retry.
      setUploadError({
        message: res.data?.detail || 'Failed to upload selfie',
        retryable: !!res.data?.retryable,
      });
      setStatus('upload_failed');
    } catch (e) {
      // Axios threw before validateStatus could see it — network error,
      // Cloudflare 520/524, or client-side timeout. All treated retryable.
      setUploadError({
        message: e?.response?.data?.detail || 'Network error uploading selfie',
        retryable: true,
      });
      setStatus('upload_failed');
    }
  }, [snapshot, uploadOnce, onCaptured, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="selfie-capture-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-pink-600" />
            {kind === 'check_in' ? 'Selfie for Clock In' : 'Selfie for Clock Out'}
          </DialogTitle>
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {cameraError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <CameraOff className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                {cameraError}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={startCamera} data-testid="selfie-retry-btn">
                    <RefreshCw className="w-3 h-3 mr-1" /> Retry camera
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    onClick={() => {
                      onCaptured?.({ url: null, fallback: true, reason: cameraError });
                      onClose();
                    }}
                    data-testid="selfie-fallback-btn"
                  >
                    Continue without selfie (admin review)
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!cameraError && status === 'starting' && (
            <div className="flex items-center justify-center bg-gray-100 rounded-lg aspect-square">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Starting camera…</p>
                <p className="text-xs text-gray-400 mt-1">This may take a few seconds</p>
              </div>
            </div>
          )}

          {!cameraError && status !== 'starting' && (
            <div className="relative bg-black rounded-lg overflow-hidden aspect-square">
              {status !== 'preview' && status !== 'uploading' && status !== 'upload_failed' && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              )}
              {(status === 'preview' || status === 'uploading' || status === 'upload_failed') && snapshot && (
                <img
                  src={snapshot}
                  alt="Captured selfie"
                  className="w-full h-full object-cover"
                  data-testid="selfie-preview"
                />
              )}
              {status === 'uploading' && (
                <div
                  className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-xs p-2"
                  data-testid="selfie-upload-progress"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span>Uploading…</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 transition-all duration-150"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {!cameraError && status === 'capturing' && (
            <button
              onClick={() => {
                onCaptured?.({ url: null, fallback: true, reason: 'preview_black_or_camera_unavailable' });
                onClose();
              }}
              className="w-full text-xs text-amber-700 underline hover:text-amber-900"
              data-testid="selfie-skip-btn"
            >
              Camera preview not showing? Continue without selfie (admin review)
            </button>
          )}

          {status === 'upload_failed' && uploadError && (
            <div
              className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900"
              data-testid="selfie-upload-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium">Upload failed</div>
                <div className="text-xs mt-0.5">{uploadError.message}</div>
                <div className="text-xs text-amber-700 mt-1">
                  Your selfie is still saved — tap Retry to send it again without retaking.
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center">
            Your selfie is stored securely and used only to verify your identity at the event.
          </p>

          <div className="flex gap-2">
            {status === 'capturing' && (
              <Button
                onClick={capture}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white"
                data-testid="selfie-capture-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Capture
              </Button>
            )}

            {status === 'preview' && (
              <>
                <Button
                  variant="outline"
                  onClick={retake}
                  data-testid="selfie-retake-btn"
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retake
                </Button>
                <Button
                  onClick={runUpload}
                  disabled={status === 'uploading'}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="selfie-use-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Use Photo
                </Button>
              </>
            )}

            {status === 'uploading' && (
              <Button disabled className="w-full" data-testid="selfie-uploading">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading… {progress}%
              </Button>
            )}

            {status === 'upload_failed' && (
              <>
                <Button
                  variant="outline"
                  onClick={retake}
                  className="flex-1"
                  data-testid="selfie-retake-after-fail-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retake
                </Button>
                <Button
                  onClick={runUpload}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="selfie-retry-upload-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry upload
                </Button>
              </>
            )}

            {(status === 'idle' || cameraError) && (
              <Button variant="outline" onClick={onClose} className="w-full" data-testid="selfie-cancel-btn">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelfieCapture;
